import React from 'react'
import { css, StyleSheet } from 'aphrodite'
import { Handle, Position } from 'reactflow'
import Tooltip from '@material-ui/core/Tooltip'
import { AppTheme } from '../../../../styles/AppTheme'
import { ChatbotNodeHeader, chatbotNodeIcon } from './ChatbotNodeHeader'
import { ChatbotTargetHandle } from './ChatbotTargetHandle'
import { HANDLE_OUT, NODE_TYPE_TEMPLATE, getOutgoingHandles } from './chatbotGraph'
import { templateNodeToBubbleData } from './chatbotTemplateForm'
import { WhatsappDocumentCard, formatWhatsappText } from './chatbotBubblePreview'
import { ChatbotBranchHandles, ChatbotDefaultHandle, partitionBranchHandles } from './ChatbotBranchHandles'

const HANDLE_STYLE = {
  width: 10,
  height: 10,
  background: AppTheme.primaryColor,
  border: '2px solid #ffffff',
}

const ROW_HANDLE_STYLE = Object.assign({}, HANDLE_STYLE, {
  top: '50%',
  right: 0,
  transform: 'translate(50%, -50%)',
})

function truncate(text, max) {
  const s = String(text || '').trim()
  if (!s) return ''
  if (s.length <= max) return s
  return s.slice(0, max) + '…'
}

function HeaderMedia(props) {
  const data = props.data || {}
  const headerType = data.headerType || 'NONE'
  if (headerType === 'TEXT') {
    const text = String(data.headerText || '').trim()
    if (!text) return null
    return <div className={css(Styles.headerText)}>{formatWhatsappText(text)}</div>
  }
  if (headerType === 'IMAGE') {
    if (!data.mediaLink) {
      return <div className={css(Styles.mediaPlaceholder)}>No image</div>
    }
    return (
      <div className={css(Styles.mediaBleed)}>
        <img src={data.mediaLink} alt='Template' className={css(Styles.mediaPreview)} />
      </div>
    )
  }
  if (headerType === 'VIDEO') {
    if (!data.mediaLink) {
      return <div className={css(Styles.mediaPlaceholder)}>No video</div>
    }
    return (
      <div className={css(Styles.mediaBleed)}>
        <video
          src={data.mediaLink}
          controls
          controlsList='nodownload noplaybackrate'
          className={css(Styles.mediaVideo) + ' nodrag nopan'}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </div>
    )
  }
  if (headerType === 'DOCUMENT') {
    if (!data.media && !data.mediaLink) {
      return <div className={css(Styles.mediaPlaceholder)}>No document</div>
    }
    return <WhatsappDocumentCard url={data.mediaLink || data.media} fileName={data.fileName} />
  }
  return null
}

function actionButtonIcon(type) {
  const t = String(type || '').toUpperCase()
  if (t === 'URL') return 'open_in_new'
  if (t === 'PHONE' || t === 'PHONE_NUMBER') return 'phone'
  if (t === 'FLOW') return 'assignment'
  return 'reply'
}

function ActionButtonsOnBubble(props) {
  const buttons = props.buttons || []
  if (!buttons.length) return null
  return (
    <div>
      {buttons.map((b, i) => (
        <div key={b.id || i} className={css(Styles.listCta)}>
          <i className={'material-icons-outlined ' + css(Styles.listCtaIcon)}>{actionButtonIcon(b.type)}</i>
          <span>{truncate(b.text, 24) || b.type}</span>
        </div>
      ))}
    </div>
  )
}

function TemplateBody(props) {
  const data = props.data || {}
  const view = templateNodeToBubbleData(data)
  if (!view.hasTemplate) {
    return (
      <div className={css(Styles.body)}>
        <div className={css(Styles.empty)}>No template key selected</div>
      </div>
    )
  }
  const bodyText = String(view.bodyText || '')
  const footerText = truncate(view.footerText, 40)
  const bleedMedia = (view.headerType === 'IMAGE' || view.headerType === 'VIDEO') && !!view.mediaLink
  return (
    <div>
      {bleedMedia ? <HeaderMedia data={view} /> : null}
      <div className={css(Styles.body)}>
        {!bleedMedia ? <HeaderMedia data={view} /> : null}
        {bodyText.trim() ? (
          <div className={css(Styles.preview)}>{formatWhatsappText(bodyText)}</div>
        ) : view.headerType === 'NONE' ? (
          <div className={css(Styles.preview)}>No text</div>
        ) : null}
        {footerText ? <div className={css(Styles.footer)}>{footerText}</div> : null}
        <ActionButtonsOnBubble buttons={view.urlButtons} />
      </div>
    </div>
  )
}

export function ChatbotTemplateNode(props) {
  const data = props.data || {}
  const issue = data.templateIssue || ''
  const handles = getOutgoingHandles(data, NODE_TYPE_TEMPLATE)
  const showOutHandle = handles.length === 1 && handles[0].id === HANDLE_OUT
  const { options, fallback } = partitionBranchHandles(showOutHandle ? [] : handles)
  const cardClass = css(
    Styles.card,
    issue && !props.selected && Styles.cardIssue,
    props.selected && !issue && Styles.cardSelected,
    props.selected && issue && Styles.cardIssueSelected
  )
  return (
    <div className={cardClass}>
      <ChatbotTargetHandle hidden={!!data.isStartNode} style={HANDLE_STYLE} />
      <ChatbotNodeHeader
        title='Send Template'
        icon={chatbotNodeIcon(NODE_TYPE_TEMPLATE)}
        isStartNode={data.isStartNode}
        onDelete={data.onDelete}
        onDuplicate={data.onDuplicate}
        onSetStartNode={data.onSetStartNode}
        onToggleDefault={options.length ? data.onToggleDefault : undefined}
        defaultEnabled={!!fallback}
      />
      <div className={css(Styles.content, fallback && Styles.contentFlush)}>
        <TemplateBody data={data} />
        {showOutHandle ? (
          <Handle
            type='source'
            position={Position.Right}
            id={HANDLE_OUT}
            style={HANDLE_STYLE}
          />
        ) : (
          <ChatbotBranchHandles handles={options} handleStyle={ROW_HANDLE_STYLE} />
        )}
      </div>
      <ChatbotDefaultHandle handle={fallback} handleStyle={ROW_HANDLE_STYLE} />
      {issue ? (
        <Tooltip title={issue} arrow placement='top' classes={{ tooltip: css(Styles.issueTooltip) }}>
          <span className={'nodrag nopan ' + css(Styles.issueBadge)}>
            <i className={'material-icons-outlined ' + css(Styles.issueBadgeIcon)}>error_outline</i>
            INVALID STEP
          </span>
        </Tooltip>
      ) : null}
    </div>
  )
}

const Styles = StyleSheet.create({
  card: {
    width: 240,
    position: 'relative',
    backgroundColor: AppTheme.secondaryBg,
    border: '1px solid ' + AppTheme.borderColor,
    borderRadius: AppTheme.borderRadius,
    overflow: 'visible',
    boxSizing: 'border-box',
  },
  cardSelected: {
    borderColor: AppTheme.primaryColor,
    boxShadow: '0 0 0 3px ' + AppTheme.primaryColor + '33',
  },
  cardIssue: {
    borderColor: '#D97706',
    boxShadow: '0 0 0 3px rgba(217, 119, 6, 0.22)',
  },
  cardIssueSelected: {
    borderColor: '#D97706',
    boxShadow: '0 0 0 2px #D97706, 0 0 0 5px ' + AppTheme.primaryColor + '40',
  },
  issueBadge: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: 20,
    padding: '0 8px 0 5px',
    gap: 4,
    borderRadius: 999,
    backgroundColor: '#D97706',
    boxShadow: '0 0 0 2px #ffffff',
    display: 'flex',
    alignItems: 'center',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    lineHeight: '20px',
    color: '#ffffff',
    whiteSpace: 'nowrap',
    zIndex: 2,
    cursor: 'default',
  },
  issueBadgeIcon: {
    fontSize: 12,
    lineHeight: 1,
    color: '#ffffff',
  },
  issueTooltip: {
    maxWidth: 240,
    fontSize: 12,
    lineHeight: '16px',
    whiteSpace: 'pre-wrap',
  },
  content: {
    backgroundColor: '#e7ffdb',
    color: '#111b21',
    borderBottomLeftRadius: AppTheme.borderRadius,
    borderBottomRightRadius: AppTheme.borderRadius,
    overflow: 'visible',
  },
  contentFlush: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  body: {
    padding: '10px 12px 12px',
    textAlign: 'left',
  },
  headerText: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: '18px',
    color: '#111b21',
    marginBottom: 6,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  preview: {
    fontSize: 13,
    lineHeight: '18px',
    color: '#111b21',
    minHeight: 18,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  empty: {
    fontSize: 13,
    lineHeight: '18px',
    color: '#6B7280',
  },
  footer: {
    marginTop: 4,
    fontSize: 11,
    lineHeight: '14px',
    color: '#667781',
    wordBreak: 'break-word',
  },
  mediaBleed: {
    width: '100%',
    overflow: 'hidden',
  },
  mediaPreview: {
    width: '100%',
    height: 'auto',
    maxHeight: 140,
    objectFit: 'cover',
    display: 'block',
  },
  mediaVideo: {
    width: '100%',
    height: 'auto',
    maxHeight: 140,
    display: 'block',
    backgroundColor: '#000',
  },
  mediaPlaceholder: {
    width: '100%',
    minHeight: 72,
    borderRadius: 6,
    backgroundColor: '#e4e6eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 6,
  },
  listCta: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid #d9efce',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
    color: '#027eb5',
  },
  listCtaIcon: {
    fontSize: 16,
    lineHeight: 1,
    color: 'inherit',
  },
})
